import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import Cookies from "js-cookie";
import DOMPurify from "dompurify";
import { useParams, useNavigate } from "react-router-dom";
import { getBugDetails, updateBugStatus } from "../../services/BugService";
import {
  getCommentsByBug,
  createComment,
  removeComment
} from "../../services/CommentService";
import {
  getAttachments,
  addAttachment,
  deleteAttachment
} from "../../services/AttachmentService";
import {
  getAssignees,
  addAssignee,
  removeAssignee
} from "../../services/BugAssigneeService";
import { getProjectMembers } from "../../services/ProjectService";
import { PRIORITY_LABELS, PRIORITY_COLORS } from "../../services/BugMetaService";
import {
  getChecklist,
  addChecklistItem,
  updateChecklistItem,
  removeChecklistItem
} from "../../services/BugChecklistService";
import {
  getLabels,
  getBugLabels,
  attachLabelToBug,
  detachLabelFromBug
} from "../../services/LabelService";
import { uploadFile } from "../../services/firebaseStorageService";
import {
  buildBugDevelopmentPrompt,
  buildBugTestScenarioPrompt
} from "../../services/BugPromptService";
import { TOKEN_COOKIE, GUEST_TOKEN } from "../../services/constants";
import { readStoredUserId } from "../../services/sessionIdentity";
import { showToast } from "../../ui/toast";
import "./BugDetail.css";
import Header from "../../components/Header/Header";
import FilePreviewModal, { detectFilePreviewKind } from "../../components/FilePreviewModal";
import {
  FiX,
  FiPaperclip,
  FiClock,
  FiUser,
  FiCheckCircle,
  FiAlertTriangle,
  FiTrash2,
  FiEdit3,
  FiCopy,
  FiCheck,
  FiRefreshCcw,
  FiActivity,
  FiUsers,
  FiUserPlus,
  FiTag,
  FiPlus,
  FiChevronDown
} from "react-icons/fi";

const htmlToPlainText = (value = "") => {
  const raw = String(value || "");
  if (!raw) return "";

  return raw
    .replace(/<li[^>]*>/gi, "- ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|ul|ol|h[1-6])>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, "\"")
    .replace(/&#39;/gi, "'")
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean)
    .join("\n")
    .trim();
};

const isBugCompleted = (item = {}) => {
  const status = String(item?.status ?? "").toLowerCase();
  const completed = item?.is_completed ?? item?.completed;
  return status === "on" || completed === true || completed === 1 || completed === "1" || completed === "on";
};

const BugDetail = ({
  bugId: propBugId,
  onClose: propOnClose,
  onEditBug,
  onStatusChange,
  projectName: propProjectName = ""
}) => {
  const params = useParams();
  const navigate = useNavigate();

  const id = propBugId ?? params.bugId;

  const [bug, setBug] = useState(null);
  const [logs, setLogs] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [openPanels, setOpenPanels] = useState({});

  const togglePanel = (key) =>
    setOpenPanels((prev) => ({ ...prev, [key]: !prev[key] }));

  const panelOpen = (key, hasContent) =>
    openPanels[key] === undefined ? !!hasContent : !!openPanels[key];

  const [comments, setComments] = useState([]);
  const [commentDraft, setCommentDraft] = useState("");
  const [commentBusy, setCommentBusy] = useState(false);
  const [commentDeleteId, setCommentDeleteId] = useState("");
  const [loading, setLoading] = useState(true);
  const [actionBusy, setActionBusy] = useState("");
  const [filePreview, setFilePreview] = useState({
    open: false,
    url: "",
    title: "",
    kind: ""
  });

  const [attachmentBusy, setAttachmentBusy] = useState(false);
  const [attachmentDeleteId, setAttachmentDeleteId] = useState("");
  const [commentFile, setCommentFile] = useState(null);

  const [assignees, setAssignees] = useState([]);
  const [members, setMembers] = useState([]);
  const [assigneeSelect, setAssigneeSelect] = useState("");
  const [assigneeBusy, setAssigneeBusy] = useState(false);
  const [assigneeRemoveId, setAssigneeRemoveId] = useState("");

  const [bugLabels, setBugLabels] = useState([]);
  const [projectLabels, setProjectLabels] = useState([]);
  const [labelSelect, setLabelSelect] = useState("");
  const [labelBusy, setLabelBusy] = useState(false);
  const [labelRemoveId, setLabelRemoveId] = useState("");

  const [checklist, setChecklist] = useState([]);
  const [checklistDraft, setChecklistDraft] = useState("");
  const [checklistBusy, setChecklistBusy] = useState(false);
  const [checklistActionId, setChecklistActionId] = useState("");

  const dialogRef = useRef(null);
  const bugFileInputRef = useRef(null);
  const commentFileInputRef = useRef(null);
  const currentUserId = readStoredUserId();
  const sessionToken = Cookies.get(TOKEN_COOKIE) || "";
  const isGuestSession = sessionToken === GUEST_TOKEN;
  const canComment = Boolean(currentUserId) && !isGuestSession;

  const formatDateTimeTR = (val) => {
    if (!val) return "";
    const raw = String(val).trim();
    let d = null;

    if (typeof val === "number") {
      d = new Date(val);
    } else {
      const isoLike = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;
      d = new Date(isoLike);
      if (Number.isNaN(d.getTime())) d = new Date(raw);
    }

    if (!d || Number.isNaN(d.getTime())) return raw;

    const datePart = d.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });

    const timePart = d.toLocaleTimeString("tr-TR", {
      hour: "2-digit",
      minute: "2-digit"
    });

    return `${datePart} • ${timePart}`;
  };

  const asTs = (val) => {
    if (!val) return 0;
    const raw = String(val).trim();
    const isoLike = raw.includes(" ") && !raw.includes("T") ? raw.replace(" ", "T") : raw;

    const d = new Date(isoLike);
    if (!Number.isNaN(d.getTime())) return d.getTime();

    const d2 = new Date(raw);
    if (!Number.isNaN(d2.getTime())) return d2.getTime();

    return 0;
  };

  useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
  }, []);

  const fetchComments = useCallback(async () => {
    if (!id) return [];
    const items = await getCommentsByBug(id);
    const baseComments = Array.isArray(items) ? items : [];

    // Her yoruma ait ekleri getir (yorum ekleri özelliği)
    const nextComments = await Promise.all(
      baseComments.map(async (comment) => {
        const commentId = String(comment?.id ?? "").trim();
        if (!commentId) return { ...comment, attachments: [] };
        try {
          const commentAttachments = await getAttachments({ commentId });
          return { ...comment, attachments: commentAttachments };
        } catch {
          return { ...comment, attachments: [] };
        }
      })
    );

    setComments(nextComments);
    return nextComments;
  }, [id]);

  const fetchAssignees = useCallback(async () => {
    if (!id) return [];
    try {
      const items = await getAssignees(id);
      const next = Array.isArray(items) ? items : [];
      setAssignees(next);
      return next;
    } catch (error) {
      console.error("Atananlar alınamadı:", error);
      setAssignees([]);
      return [];
    }
  }, [id]);

  const fetchBugLabels = useCallback(async () => {
    if (!id) return [];
    try {
      const items = await getBugLabels(id);
      const next = Array.isArray(items) ? items : [];
      setBugLabels(next);
      return next;
    } catch (error) {
      console.error("Etiketler alınamadı:", error);
      setBugLabels([]);
      return [];
    }
  }, [id]);

  const fetchChecklist = useCallback(async () => {
    if (!id) return [];
    try {
      const items = await getChecklist(id);
      const next = Array.isArray(items) ? items : [];
      setChecklist(next);
      return next;
    } catch (error) {
      console.error("Checklist alınamadı:", error);
      setChecklist([]);
      return [];
    }
  }, [id]);

  const fetchBugDetails = useCallback(async (options = {}) => {
    if (!id) return;
    const { showLoader = true } = options;

    if (showLoader) {
      setLoading(true);
    }

    try {
      const [detailResult, commentsResult] = await Promise.allSettled([
        getBugDetails(id),
        fetchComments(),
        fetchAssignees(),
        fetchBugLabels(),
        fetchChecklist()
      ]);

      if (detailResult.status === "fulfilled") {
        const data = detailResult.value || {};
        setBug(data.bug || null);
        setLogs(Array.isArray(data.logs) ? data.logs : []);
        setAttachments(Array.isArray(data.attachments) ? data.attachments : []);
      } else {
        throw detailResult.reason;
      }

      if (commentsResult.status === "rejected") {
        console.error("Yorumlar alınamadı:", commentsResult.reason);
      }
    } catch (error) {
      console.error("Bug detayları alınamadı:", error);
      setBug(null);
      setLogs([]);
      setAttachments([]);
      setComments([]);
      setAssignees([]);
      setBugLabels([]);
      setChecklist([]);
    } finally {
      if (showLoader) {
        setLoading(false);
      }
    }
  }, [id, fetchComments, fetchAssignees, fetchBugLabels, fetchChecklist]);

  useEffect(() => {
    fetchBugDetails();
  }, [fetchBugDetails]);

  useEffect(() => {
    const onFocus = () => {
      fetchBugDetails({ showLoader: false });
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        fetchBugDetails({ showLoader: false });
      }
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [fetchBugDetails]);

  const handleClose = useCallback(() => {
    if (typeof propOnClose === "function") {
      propOnClose();
    } else {
      navigate(-1);
    }
  }, [propOnClose, navigate]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && handleClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [handleClose]);

  const headerButtons = [];

  const statusInfo = (status) => {
    if (status === "on") {
      return {
        label: "Tamamlandı",
        className: "completed",
        icon: <FiCheckCircle size={14} aria-hidden="true" />
      };
    }
    return {
      label: "Devam Ediyor",
      className: "incomplete",
      icon: <FiAlertTriangle size={14} aria-hidden="true" />
    };
  };

  const fileNameFromPath = useCallback((p) => {
    try {
      if (!p) return "Dosya";
      const url = new URL(p, window.location.origin);
      const parts = url.pathname.split("/");
      const last = parts[parts.length - 1] || "Dosya";
      return decodeURIComponent(last);
    } catch {
      const parts = String(p).split("/");
      return decodeURIComponent(parts[parts.length - 1] || "Dosya");
    }
  }, []);

  const openFilePreview = useCallback((url, title) => {
    const value = String(url || "").trim();
    if (!value) return;
    setFilePreview({
      open: true,
      url: value,
      title: title || fileNameFromPath(value),
      kind: detectFilePreviewKind(value)
    });
  }, [fileNameFromPath]);

  const copyText = useCallback(async (text, feedback = {}) => {
    const value = String(text || "").trim();
    if (!value) {
      showToast({
        title: feedback.emptyTitle || "Kopyalanacak içerik yok",
        description: feedback.emptyDescription || "Bu işlem için yeterli içerik bulunamadı.",
        variant: "error",
        position: "bottom-right"
      });
      return false;
    }

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.setAttribute("readonly", "readonly");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
      }

      showToast({
        title: feedback.successTitle || "Kopyalandı",
        description: feedback.successDescription || "İçerik panoya alındı.",
        variant: "success",
        position: "bottom-right"
      });
      return true;
    } catch (error) {
      console.error("Kopyalama başarısız:", error);
      showToast({
        title: feedback.errorTitle || "Kopyalama başarısız",
        description: feedback.errorDescription || "Tarayıcı panoya erişemedi.",
        variant: "error",
        position: "bottom-right"
      });
      return false;
    }
  }, []);

  const displayedLogs = useMemo(() => {
    const base = Array.isArray(logs) ? logs : [];
    if (!bug) return base;

    const createdRaw = bug.created_at || "";
    const updatedRaw = bug.updated_at || "";

    const createdTs = asTs(createdRaw);
    const updatedTs = asTs(updatedRaw);

    const hasUpdatedMeaningfully =
      !!updatedTs &&
      !!createdTs &&
      updatedTs > createdTs + 30000;

    const existsAdded = base.some((l) => {
      const act = String(l?.action || "").toLowerCase();
      return act === "added" || act === "created";
    });

    const existsUpdated = base.some((l) => {
      const act = String(l?.action || "").toLowerCase();
      return act === "updated" || act === "edited";
    });

    const whoAdded =
      bug.creator_name ||
      bug.creator_email ||
      bug.created_by_name ||
      bug.created_by ||
      "Sistem";

    const whoUpdated =
      bug.updated_by_name ||
      bug.updater_name ||
      bug.updated_by ||
      bug.creator_name ||
      bug.creator_email ||
      "Sistem";

    const mapped = base.map((l) => {
      const t = asTs(l?.created_at) || 0;
      return {
        ...l,
        __ts: t || Date.now(),
        created_at_formatted: l?.created_at ? formatDateTimeTR(l.created_at) : ""
      };
    });

    const extras = [];

    if (createdRaw && !existsAdded) {
      extras.push({
        action: "added",
        user_name: whoAdded,
        created_at: createdRaw,
        created_at_formatted: formatDateTimeTR(createdRaw),
        __ts: createdTs || Date.now(),
        __synthetic: true
      });
    }

    if (updatedRaw && hasUpdatedMeaningfully && !existsUpdated) {
      extras.push({
        action: "updated",
        user_name: whoUpdated,
        created_at: updatedRaw,
        created_at_formatted: formatDateTimeTR(updatedRaw),
        __ts: updatedTs || Date.now(),
        __synthetic: true
      });
    }

    const all = [...mapped, ...extras];

    all.sort((a, b) => {
      const at = a?.__ts || 0;
      const bt = b?.__ts || 0;
      return bt - at;
    });

    return all;
  }, [logs, bug]);

  const completed = useMemo(() => isBugCompleted(bug), [bug]);
  const detailStatus = bug ? statusInfo(completed ? "on" : "off") : null;
  const bugProjectId = bug?.project_id || bug?.projectId || bug?.uniq_id || bug?.uniq || "";
  const detailProjectName = propProjectName || bug?.project_name || bug?.projectName || "";
  const detailVersionName = bug?.version_name || bug?.version || "";

  useEffect(() => {
    if (!bugProjectId) {
      setMembers([]);
      return;
    }
    let alive = true;
    getProjectMembers(bugProjectId)
      .then((list) => {
        if (alive) setMembers(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (alive) setMembers([]);
      });
    return () => {
      alive = false;
    };
  }, [bugProjectId]);

  useEffect(() => {
    if (!bugProjectId) {
      setProjectLabels([]);
      return;
    }
    let alive = true;
    getLabels(bugProjectId)
      .then((list) => {
        if (alive) setProjectLabels(Array.isArray(list) ? list : []);
      })
      .catch(() => {
        if (alive) setProjectLabels([]);
      });
    return () => {
      alive = false;
    };
  }, [bugProjectId]);

  const attachedLabelIds = useMemo(
    () => new Set(bugLabels.map((l) => String(l?.id ?? "").trim()).filter(Boolean)),
    [bugLabels]
  );

  const availableLabels = useMemo(
    () =>
      projectLabels.filter(
        (l) => String(l?.id ?? "").trim() && !attachedLabelIds.has(String(l.id).trim())
      ),
    [projectLabels, attachedLabelIds]
  );

  const handleAddLabel = useCallback(async () => {
    const labelId = String(labelSelect || "").trim();
    if (!labelId) return;

    if (!canComment) {
      showToast({
        title: "Etiket eklenemedi",
        description: "İşe etiket eklemek için giriş yapman gerekiyor.",
        variant: "error"
      });
      return;
    }

    setLabelBusy(true);
    try {
      await attachLabelToBug({ bugId: id, labelId });
      await fetchBugLabels();
      setLabelSelect("");
      showToast({
        title: "Etiket eklendi",
        description: "İşe etiket eklendi."
      });
    } catch (error) {
      console.error("Etiket eklenemedi:", error);
      showToast({
        title: "Etiket eklenemedi",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setLabelBusy(false);
    }
  }, [labelSelect, canComment, id, fetchBugLabels]);

  const handleRemoveLabel = useCallback(async (labelId) => {
    const normalizedId = String(labelId || "").trim();
    if (!normalizedId) return;

    setLabelRemoveId(normalizedId);
    try {
      await detachLabelFromBug({ bugId: id, labelId: normalizedId });
      await fetchBugLabels();
      showToast({
        title: "Etiket kaldırıldı",
        description: "Liste hemen yenilendi."
      });
    } catch (error) {
      console.error("Etiket kaldırılamadı:", error);
      showToast({
        title: "Etiket kaldırılamadı",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setLabelRemoveId("");
    }
  }, [id, fetchBugLabels]);

  const checklistDone = useMemo(
    () => checklist.filter((item) => Number(item?.is_done) === 1 || item?.is_done === true).length,
    [checklist]
  );
  const checklistProgress = checklist.length
    ? Math.round((checklistDone / checklist.length) * 100)
    : 0;

  const handleAddChecklistItem = useCallback(async (event) => {
    if (event) event.preventDefault();
    const title = String(checklistDraft || "").trim();
    if (!title) return;

    if (!canComment) {
      showToast({
        title: "Madde eklenemedi",
        description: "Madde eklemek için giriş yapman gerekiyor.",
        variant: "error"
      });
      return;
    }

    setChecklistBusy(true);
    try {
      await addChecklistItem({ bugId: id, title });
      await fetchChecklist();
      setChecklistDraft("");
    } catch (error) {
      console.error("Madde eklenemedi:", error);
      showToast({
        title: "Madde eklenemedi",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setChecklistBusy(false);
    }
  }, [checklistDraft, canComment, id, fetchChecklist]);

  const handleToggleChecklistItem = useCallback(async (item) => {
    const itemId = String(item?.id ?? "").trim();
    if (!itemId) return;

    const nextDone = !(Number(item?.is_done) === 1 || item?.is_done === true);
    // Optimistik güncelle
    setChecklist((prev) =>
      prev.map((it) => (String(it.id) === itemId ? { ...it, is_done: nextDone ? 1 : 0 } : it))
    );

    try {
      await updateChecklistItem({ id: itemId, isDone: nextDone });
    } catch (error) {
      console.error("Madde güncellenemedi:", error);
      await fetchChecklist();
      showToast({
        title: "Madde güncellenemedi",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    }
  }, [fetchChecklist]);

  const handleDeleteChecklistItem = useCallback(async (itemId) => {
    const normalizedId = String(itemId || "").trim();
    if (!normalizedId) return;

    setChecklistActionId(normalizedId);
    try {
      await removeChecklistItem({ id: normalizedId });
      await fetchChecklist();
    } catch (error) {
      console.error("Madde silinemedi:", error);
      showToast({
        title: "Madde silinemedi",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setChecklistActionId("");
    }
  }, [fetchChecklist]);

  const memberId = (member) =>
    String(member?.user_id ?? member?.id ?? member?.member_id ?? member?.uid ?? "").trim();

  const assignedUserIds = useMemo(
    () => new Set(assignees.map((a) => String(a?.user_id ?? "").trim()).filter(Boolean)),
    [assignees]
  );

  const availableMembers = useMemo(
    () => members.filter((m) => memberId(m) && !assignedUserIds.has(memberId(m))),
    [members, assignedUserIds]
  );

  const handleAssign = useCallback(async () => {
    const userId = String(assigneeSelect || "").trim();
    if (!userId) return;

    if (!canComment) {
      showToast({
        title: "Atama yapılamadı",
        description: "İşe kullanıcı atamak için giriş yapman gerekiyor.",
        variant: "error"
      });
      return;
    }

    setAssigneeBusy(true);
    try {
      await addAssignee({ bugId: id, userId, assignedBy: currentUserId });
      await fetchAssignees();
      setAssigneeSelect("");
      showToast({
        title: "Kullanıcı atandı",
        description: "İş, seçilen kullanıcıya atandı."
      });
    } catch (error) {
      console.error("Atama yapılamadı:", error);
      showToast({
        title: "Atama yapılamadı",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setAssigneeBusy(false);
    }
  }, [assigneeSelect, canComment, id, currentUserId, fetchAssignees]);

  const handleUnassign = useCallback(async (userId) => {
    const normalizedId = String(userId || "").trim();
    if (!normalizedId) return;

    setAssigneeRemoveId(normalizedId);
    try {
      await removeAssignee({ bugId: id, userId: normalizedId });
      await fetchAssignees();
      showToast({
        title: "Atama kaldırıldı",
        description: "Liste hemen yenilendi."
      });
    } catch (error) {
      console.error("Atama kaldırılamadı:", error);
      showToast({
        title: "Atama kaldırılamadı",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setAssigneeRemoveId("");
    }
  }, [id, fetchAssignees]);

  const getCurrentBugUrl = () => {
    if (typeof window === "undefined") return "";

    try {
      const current = new URL(window.location.href);
      if (propBugId) {
        current.searchParams.set("bug", String(id));
        return current.toString();
      }
      return `${window.location.origin}/bug_detail/${encodeURIComponent(String(id || ""))}`;
    } catch {
      return "";
    }
  };

  const handleStatusToggle = async () => {
    if (!bug || actionBusy) return;

    const nextCompleted = !completed;
    setActionBusy("status");

    try {
      await updateBugStatus(id, nextCompleted, bugProjectId, currentUserId);
      const nextBug = {
        ...bug,
        status: nextCompleted ? "on" : "off",
        is_completed: nextCompleted
      };

      setBug(nextBug);
      await fetchBugDetails({ showLoader: false });

      if (typeof onStatusChange === "function") {
        await onStatusChange({
          bugId: id,
          isCompleted: nextCompleted,
          bug: nextBug
        });
      }

      showToast({
        title: nextCompleted ? "İş tamamlandı" : "İş yeniden açıldı",
        description: "Detay bilgisi ve liste durumu güncellendi.",
        variant: "success",
        position: "bottom-right"
      });
    } catch (error) {
      console.error("Hızlı durum güncelleme başarısız:", error);
      showToast({
        title: "Durum güncellenemedi",
        description: error?.message || "İş durumu değiştirilirken bir sorun oluştu.",
        variant: "error",
        position: "bottom-right"
      });
    } finally {
      setActionBusy("");
    }
  };

  const handleEditBug = () => {
    if (!bug || typeof onEditBug !== "function") return;
    onEditBug({
      ...bug,
      version_name: detailVersionName
    });
  };

  const handleCopyPrompt = async (kind) => {
    if (!bug || actionBusy) return;

    const isTestPrompt = kind === "test";
    const builder = isTestPrompt ? buildBugTestScenarioPrompt : buildBugDevelopmentPrompt;

    setActionBusy(kind);
    try {
      const prompt = builder({
        bug: {
          ...bug,
          is_completed: completed
        },
        projectId: bugProjectId,
        projectName: detailProjectName,
        versionName: detailVersionName,
        attachments,
        currentUrl: getCurrentBugUrl(),
        prefix: `Detay #${id}`
      });

      await copyText(prompt, {
        successTitle: isTestPrompt ? "Test senaryosu hazır" : "Geliştirme promptu hazır",
        successDescription: isTestPrompt
          ? "İşe göre test senaryosu promptu panoya kopyalandı."
          : "Uygulama planı odaklı prompt panoya kopyalandı."
      });
    } finally {
      setActionBusy("");
    }
  };

  const handleCopySummary = async () => {
    if (!bug || actionBusy) return;

    const summary = [
      `Başlık: ${bug.title || "-"}`,
      `Durum: ${completed ? "Tamamlandı" : "Devam Ediyor"}`,
      detailProjectName ? `Proje: ${detailProjectName}` : "",
      detailVersionName ? `Sürüm: ${detailVersionName}` : "",
      `Oluşturan: ${bug.creator_email || bug.creator_name || bug.created_by || "-"}`,
      `Oluşturma: ${formatDateTimeTR(bug.created_at) || "-"}`,
      `Güncelleme: ${formatDateTimeTR(bug.updated_at) || "-"}`,
      `Ek sayısı: ${attachments.length}`,
      `Yorum sayısı: ${comments.length}`,
      getCurrentBugUrl() ? `Bağlantı: ${getCurrentBugUrl()}` : "",
      "",
      "Açıklama:",
      htmlToPlainText(bug.description || "") || "Açıklama eklenmemiş."
    ]
      .filter((line) => line !== "")
      .join("\n");

    setActionBusy("copy");
    try {
      await copyText(summary, {
        successTitle: "İş özeti kopyalandı",
        successDescription: "Detay bilgileri panoya alındı."
      });
    } finally {
      setActionBusy("");
    }
  };

  const handleCommentSubmit = useCallback(async (event) => {
    event.preventDefault();

    const content = String(commentDraft || "").trim();
    if (!content) {
      showToast({
        title: "Yorum boş olamaz",
        description: "Göndermeden önce kısa bir not ekle.",
        variant: "error"
      });
      return;
    }

    if (!canComment) {
      showToast({
        title: "Yorum eklenemedi",
        description: "Yorum eklemek için giriş yapman gerekiyor.",
        variant: "error"
      });
      return;
    }

    setCommentBusy(true);
    try {
      const commentResponse = await createComment({
        bugId: id,
        userId: currentUserId,
        content
      });

      // Yoruma dosya iliştirilmişse: Firebase'e yükle + /attachments'a kaydet
      const newCommentId =
        commentResponse?.comment_id ??
        commentResponse?.id ??
        commentResponse?.data?.comment_id ??
        "";

      if (commentFile && newCommentId) {
        try {
          const url = await uploadFile(commentFile);
          await addAttachment({
            commentId: newCommentId,
            filePath: url,
            uploadedBy: currentUserId
          });
        } catch (attachError) {
          showToast({
            title: "Yorum eklendi, ek yüklenemedi",
            description: attachError?.message || "Dosya yüklenirken bir sorun oluştu.",
            variant: "error"
          });
        }
      }

      await fetchBugDetails({ showLoader: false });
      setCommentDraft("");
      setCommentFile(null);
      if (commentFileInputRef.current) commentFileInputRef.current.value = "";
      showToast({
        title: "Yorum eklendi",
        description: "Ekip notu kayda geçti."
      });
    } catch (error) {
      console.error("Yorum eklenemedi:", error);
      showToast({
        title: "Yorum eklenemedi",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setCommentBusy(false);
    }
  }, [canComment, commentDraft, commentFile, currentUserId, fetchBugDetails, id]);

  const handleCommentDelete = useCallback(async (commentId) => {
    const normalizedId = String(commentId || "").trim();
    if (!normalizedId) return;

    setCommentDeleteId(normalizedId);
    try {
      await removeComment({ commentId: normalizedId });
      await fetchComments();
      showToast({
        title: "Yorum silindi",
        description: "Liste hemen yenilendi."
      });
    } catch (error) {
      console.error("Yorum silinemedi:", error);
      showToast({
        title: "Yorum silinemedi",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setCommentDeleteId("");
    }
  }, [fetchComments]);

  const handleBugAttachmentUpload = useCallback(async (event) => {
    const file = event.target.files?.[0];
    if (event.target) event.target.value = "";
    if (!file) return;

    if (!canComment) {
      showToast({
        title: "Ek eklenemedi",
        description: "Dosya eklemek için giriş yapman gerekiyor.",
        variant: "error"
      });
      return;
    }

    setAttachmentBusy(true);
    try {
      const url = await uploadFile(file);
      await addAttachment({ bugId: id, filePath: url, uploadedBy: currentUserId });
      await fetchBugDetails({ showLoader: false });
      showToast({ title: "Ek yüklendi", description: file.name });
    } catch (error) {
      console.error("Ek yüklenemedi:", error);
      showToast({
        title: "Ek yüklenemedi",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setAttachmentBusy(false);
    }
  }, [canComment, id, currentUserId, fetchBugDetails]);

  const handleAttachmentDelete = useCallback(async (attachmentId) => {
    const aid = String(attachmentId || "").trim();
    if (!aid) return;

    setAttachmentDeleteId(aid);
    try {
      await deleteAttachment(aid);
      await fetchBugDetails({ showLoader: false });
      showToast({ title: "Ek silindi", description: "Liste hemen yenilendi." });
    } catch (error) {
      console.error("Ek silinemedi:", error);
      showToast({
        title: "Ek silinemedi",
        description: error?.message || "Bir sorun oluştu.",
        variant: "error"
      });
    } finally {
      setAttachmentDeleteId("");
    }
  }, [fetchBugDetails]);

  return (
    <>
      <Header buttons={headerButtons} />
      <div
        className="bugdetail-overlay"
        onMouseDown={(e) => {
          if (e.target.classList.contains("bugdetail-overlay")) handleClose();
        }}
      >
        <div
          ref={dialogRef}
          className="bugdetail-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="bugdetail-title"
          tabIndex={-1}
        >
          <button
            type="button"
            className="bugdetail-close"
            aria-label="Kapat"
            onClick={handleClose}
          >
            <FiX size={18} />
            <span>Kapat</span>
          </button>

          <div className="bug-detail-container" aria-busy={loading ? "true" : "false"}>
            {loading && (
              <div className="skeleton">
                <div className="sk-title" />
                <div className="sk-row" />
                <div className="sk-row short" />
                <div className="sk-block" />
                <div className="sk-block" />
              </div>
            )}

            {!loading && bug && (
              <>
                <header className="bug-header">
                  <div className="bug-header-copy">
                    <span className="bug-header-kicker">Buglog Detay</span>
                    <h1 id="bugdetail-title" title={bug.title || ""}>
                      {bug.title}
                    </h1>
                    <p className="bug-header-subtitle">
                      İş içeriği, ekler ve işlem geçmişi tek ekranda toplanır.
                    </p>
                  </div>

                  <div className="bug-header-side">
                    <span
                      className={`status-badge ${detailStatus?.className || ""}`}
                      aria-live="polite"
                    >
                      {detailStatus?.icon}
                      <span className="status-text">{detailStatus?.label}</span>
                    </span>

                    <div className="bug-header-stats">
                      <div className="bug-header-stat">
                        <strong>{attachments.length}</strong>
                        <span>Ek</span>
                      </div>
                      <div className="bug-header-stat">
                        <strong>{displayedLogs.length}</strong>
                        <span>Hareket</span>
                      </div>
                    </div>
                  </div>
                </header>

                <section className="bug-quick-actions" aria-label="Hızlı aksiyonlar">
                  <button
                    type="button"
	                    className={`bug-quick-action ${completed ? "neutral" : "success"}`}
	                    onClick={handleStatusToggle}
	                    disabled={Boolean(actionBusy)}
                  >
                    {completed ? <FiRefreshCcw aria-hidden="true" /> : <FiCheck aria-hidden="true" />}
                    <span>
                      {actionBusy === "status"
                        ? "Güncelleniyor..."
                        : completed
                          ? "Yeniden Aç"
                          : "Tamamla"}
                    </span>
                  </button>

                  {typeof onEditBug === "function" && (
                    <button
	                      type="button"
	                      className="bug-quick-action primary"
	                      onClick={handleEditBug}
	                      disabled={Boolean(actionBusy)}
	                    >
                      <FiEdit3 aria-hidden="true" />
                      <span>Düzenle</span>
                    </button>
                  )}

                  <button
                    type="button"
	                    className="bug-quick-action"
	                    onClick={() => handleCopyPrompt("dev")}
	                    disabled={Boolean(actionBusy)}
                  >
                    <FiActivity aria-hidden="true" />
                    <span>{actionBusy === "dev" ? "Hazırlanıyor..." : "Geliştir"}</span>
                  </button>

                  <button
                    type="button"
	                    className="bug-quick-action"
	                    onClick={() => handleCopyPrompt("test")}
	                    disabled={Boolean(actionBusy)}
                  >
                    <FiCheckCircle aria-hidden="true" />
                    <span>{actionBusy === "test" ? "Hazırlanıyor..." : "Test"}</span>
                  </button>

                  <button
                    type="button"
	                    className="bug-quick-action"
	                    onClick={handleCopySummary}
	                    disabled={Boolean(actionBusy)}
                  >
                    <FiCopy aria-hidden="true" />
                    <span>{actionBusy === "copy" ? "Kopyalanıyor..." : "Özeti Kopyala"}</span>
                  </button>

                </section>

                <section className="bug-meta">
                  <div className="bug-meta-item is-pill">
                    <FiUser aria-hidden="true" />
                    <div>
                      <div className="meta-label">Oluşturan</div>
                      <div className="meta-value">{bug.creator_email}</div>
                    </div>
                  </div>

                  {bug.created_at && (
                    <div className="bug-meta-item is-pill is-date">
                      <FiClock aria-hidden="true" />
                      <div>
                        <div className="meta-label">Oluşturma</div>
                        <div className="meta-value">{formatDateTimeTR(bug.created_at)}</div>
                      </div>
                    </div>
                  )}

                  {bug.updated_at && (
                    <div className="bug-meta-item is-pill is-date">
                      <FiClock aria-hidden="true" />
                      <div>
                        <div className="meta-label">Güncelleme</div>
                        <div className="meta-value">{formatDateTimeTR(bug.updated_at)}</div>
                      </div>
                    </div>
                  )}

                  {bug.meta?.priority && (
                    <div className="bug-meta-item is-pill">
                      <FiAlertTriangle aria-hidden="true" />
                      <div>
                        <div className="meta-label">Öncelik</div>
                        <div
                          className="meta-value"
                          style={{ color: PRIORITY_COLORS[bug.meta.priority] || undefined }}
                        >
                          {PRIORITY_LABELS[bug.meta.priority] || bug.meta.priority}
                        </div>
                      </div>
                    </div>
                  )}

                  {bug.meta?.due_date && (
                    <div className="bug-meta-item is-pill is-date">
                      <FiClock aria-hidden="true" />
                      <div>
                        <div className="meta-label">Teslim Tarihi</div>
                        <div className="meta-value">{formatDateTimeTR(bug.meta.due_date)}</div>
                      </div>
                    </div>
                  )}
                </section>

                <section
                  className={`bug-panel bug-labels-panel is-foldable${bugLabels.length ? "" : " is-hidden"}${panelOpen("labels", bugLabels.length) ? " is-open" : " is-collapsed"}`}
                >
                  <button
                    type="button"
                    className="bug-panel-head bug-panel-toggle"
                    onClick={() => togglePanel("labels")}
                    aria-expanded={panelOpen("labels", bugLabels.length)}
                  >
                    <h2>Etiketler</h2>
                    <span className="bug-panel-chip">{bugLabels.length} etiket</span>
                    <FiChevronDown className="bug-panel-caret" size={15} aria-hidden="true" />
                  </button>

                  {bugLabels.length > 0 ? (
                    <ul className="buglabel-list">
                      {bugLabels.map((label) => {
                        const labelId = String(label?.id ?? "").trim();
                        const color = label?.color || "#489bf4";
                        const isRemoving = labelRemoveId === labelId;
                        return (
                          <li
                            key={labelId || label?.bug_label_id}
                            className="buglabel-chip"
                            style={{
                              backgroundColor: `${color}1a`,
                              color,
                              borderColor: `${color}40`
                            }}
                          >
                            <span className="buglabel-dot" style={{ backgroundColor: color }} />
                            <span className="buglabel-name">{label?.name}</span>
                            {canComment && labelId && (
                              <button
                                type="button"
                                className="buglabel-remove"
                                onClick={() => handleRemoveLabel(labelId)}
                                disabled={isRemoving}
                                aria-label="Etiketi kaldır"
                                title="Etiketi kaldır"
                                style={{ color }}
                              >
                                <FiX size={13} aria-hidden="true" />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="empty">Bu işe henüz etiket eklenmemiş.</p>
                  )}

                  {canComment && (
                    <div className="buglabel-add">
                      <span className="buglabel-add-icon" aria-hidden="true">
                        <FiTag size={15} />
                      </span>
                      <select
                        className="buglabel-select"
                        value={labelSelect}
                        onChange={(event) => setLabelSelect(event.target.value)}
                        disabled={labelBusy || availableLabels.length === 0}
                      >
                        <option value="">
                          {projectLabels.length === 0
                            ? "Önce projeye etiket ekleyin"
                            : availableLabels.length === 0
                              ? "Eklenebilecek etiket yok"
                              : "Etiket seç..."}
                        </option>
                        {availableLabels.map((label) => (
                          <option key={label.id} value={label.id}>
                            {label.name}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="buglabel-add-btn"
                        onClick={handleAddLabel}
                        disabled={labelBusy || !labelSelect}
                      >
                        <FiPlus size={14} aria-hidden="true" />
                        <span>{labelBusy ? "Ekleniyor..." : "Ekle"}</span>
                      </button>
                    </div>
                  )}
                </section>

                <section
                  className={`bug-panel bug-checklist is-foldable${checklist.length ? "" : " is-hidden"}${panelOpen("checklist", checklist.length) ? " is-open" : " is-collapsed"}`}
                >
                  <button
                    type="button"
                    className="bug-panel-head bug-panel-toggle"
                    onClick={() => togglePanel("checklist")}
                    aria-expanded={panelOpen("checklist", checklist.length)}
                  >
                    <h2>Kontrol Listesi</h2>
                    <span className="bug-panel-chip">
                      {checklist.length > 0 ? `${checklistDone}/${checklist.length}` : "0"}
                    </span>
                    <FiChevronDown className="bug-panel-caret" size={15} aria-hidden="true" />
                  </button>

                  {checklist.length > 0 && (
                    <div className="checklist-progress" aria-hidden="true">
                      <div
                        className="checklist-progress-bar"
                        style={{ width: `${checklistProgress}%` }}
                      />
                    </div>
                  )}

                  {checklist.length > 0 ? (
                    <ul className="checklist-list">
                      {checklist.map((item) => {
                        const itemId = String(item?.id ?? "").trim();
                        const done = Number(item?.is_done) === 1 || item?.is_done === true;
                        const isActing = checklistActionId === itemId;
                        return (
                          <li key={itemId} className={`checklist-item${done ? " done" : ""}`}>
                            <button
                              type="button"
                              className="checklist-check"
                              onClick={() => canComment && handleToggleChecklistItem(item)}
                              disabled={!canComment}
                              aria-label={done ? "Tamamlanmadı işaretle" : "Tamamlandı işaretle"}
                            >
                              {done ? <FiCheckCircle size={16} /> : <span className="checklist-check-empty" />}
                            </button>
                            <span className="checklist-title">{item.title}</span>
                            {canComment && itemId && (
                              <button
                                type="button"
                                className="checklist-delete"
                                onClick={() => handleDeleteChecklistItem(itemId)}
                                disabled={isActing}
                                aria-label="Maddeyi sil"
                                title="Maddeyi sil"
                              >
                                <FiTrash2 size={13} aria-hidden="true" />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="empty">Bu işe henüz kontrol maddesi eklenmemiş.</p>
                  )}

                  {canComment && (
                    <form className="checklist-add" onSubmit={handleAddChecklistItem}>
                      <input
                        type="text"
                        className="checklist-input"
                        placeholder="Yeni madde ekle..."
                        value={checklistDraft}
                        onChange={(event) => setChecklistDraft(event.target.value)}
                        disabled={checklistBusy}
                        maxLength={500}
                      />
                      <button
                        type="submit"
                        className="checklist-add-btn"
                        disabled={checklistBusy || !String(checklistDraft || "").trim()}
                      >
                        {checklistBusy ? "Ekleniyor..." : "Ekle"}
                      </button>
                    </form>
                  )}
                </section>

                <section
                  className={`bug-panel bug-assignees is-foldable${assignees.length ? "" : " is-hidden"}${panelOpen("assignees", assignees.length) ? " is-open" : " is-collapsed"}`}
                >
                  <button
                    type="button"
                    className="bug-panel-head bug-panel-toggle"
                    onClick={() => togglePanel("assignees")}
                    aria-expanded={panelOpen("assignees", assignees.length)}
                  >
                    <h2>Atananlar</h2>
                    <span className="bug-panel-chip">{assignees.length} kişi</span>
                    <FiChevronDown className="bug-panel-caret" size={15} aria-hidden="true" />
                  </button>

                  {canComment && (
                    <div className="assignee-add">
                      <span className="assignee-add-icon" aria-hidden="true">
                        <FiUsers size={16} />
                      </span>
                      <select
                        className="assignee-select"
                        value={assigneeSelect}
                        onChange={(event) => setAssigneeSelect(event.target.value)}
                        disabled={assigneeBusy || availableMembers.length === 0}
                      >
                        <option value="">
                          {availableMembers.length === 0
                            ? "Atanabilecek kullanıcı yok"
                            : "Kullanıcı seç..."}
                        </option>
                        {availableMembers.map((member) => (
                          <option key={memberId(member)} value={memberId(member)}>
                            {member.name || member.email || `Kullanıcı #${memberId(member)}`}
                          </option>
                        ))}
                      </select>
                      <button
                        type="button"
                        className="assignee-add-btn"
                        onClick={handleAssign}
                        disabled={assigneeBusy || !assigneeSelect}
                      >
                        <FiUserPlus size={14} aria-hidden="true" />
                        <span>{assigneeBusy ? "Atanıyor..." : "Ata"}</span>
                      </button>
                    </div>
                  )}

                  {assignees.length > 0 ? (
                    <ul className="assignee-list">
                      {assignees.map((assignee) => {
                        const assigneeUserId = String(assignee?.user_id ?? "").trim();
                        const isRemoving = assigneeRemoveId === assigneeUserId;
                        const displayName = assignee.name || assignee.email || `Kullanıcı #${assigneeUserId}`;
                        const initial = String(displayName).trim().charAt(0).toUpperCase() || "?";
                        return (
                          <li key={assignee.id || assigneeUserId} className="assignee-item">
                            <span className="assignee-avatar" aria-hidden="true">{initial}</span>
                            <div className="assignee-info">
                              <span className="assignee-name">{displayName}</span>
                              {assignee.email && assignee.name && (
                                <span className="assignee-email">{assignee.email}</span>
                              )}
                            </div>
                            {canComment && assigneeUserId && (
                              <button
                                type="button"
                                className="assignee-remove"
                                onClick={() => handleUnassign(assigneeUserId)}
                                disabled={isRemoving}
                                aria-label="Atamayı kaldır"
                                title="Atamayı kaldır"
                              >
                                <FiX size={14} aria-hidden="true" />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="empty">Bu işe henüz kimse atanmamış.</p>
                  )}
                </section>

                <section className="bug-panel bug-info">
                  <div className="bug-panel-head">
                    <h2>Açıklama</h2>
                    <span className="bug-panel-chip">
                      {bug.description ? "Detay mevcut" : "Detay yok"}
                    </span>
                  </div>
                  <div
                    className="bug-description"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(bug.description || "<p>Bu iş için açıklama eklenmemiş.</p>") }}
                  />
                </section>

                <section className="bug-panel bug-attachments">
                  <div className="bug-panel-head">
                    <h2>Ekler</h2>
                    <span className="bug-panel-chip">{attachments.length} dosya</span>
                    {canComment && (
                      <>
                        <input
                          ref={bugFileInputRef}
                          type="file"
                          hidden
                          onChange={handleBugAttachmentUpload}
                        />
                        <button
                          type="button"
                          className="bug-attachment-add"
                          onClick={() => bugFileInputRef.current?.click()}
                          disabled={attachmentBusy}
                        >
                          <FiPaperclip size={14} aria-hidden="true" />
                          <span>{attachmentBusy ? "Yükleniyor..." : "Ek ekle"}</span>
                        </button>
                      </>
                    )}
                  </div>
                  {attachments.length > 0 ? (
                    <ul className="attachment-list">
                      {attachments.map((attachment) => {
                        const attId = String(attachment?.id ?? "").trim();
                        const isDeleting = attachmentDeleteId === attId;
                        return (
                          <li key={attachment.id} className="attachment-item">
                            <FiPaperclip className="att-icon" aria-hidden="true" />
                            <button
                              type="button"
                              className="attachment-preview-button"
                              title={attachment.file_path}
                              onClick={() =>
                                openFilePreview(
                                  attachment.file_path,
                                  fileNameFromPath(attachment.file_path)
                                )
                              }
                            >
                              {fileNameFromPath(attachment.file_path)}
                            </button>
                            {canComment && attId && (
                              <button
                                type="button"
                                className="attachment-delete"
                                onClick={() => handleAttachmentDelete(attId)}
                                disabled={isDeleting}
                                aria-label="Eki sil"
                                title="Eki sil"
                              >
                                <FiTrash2 size={13} aria-hidden="true" />
                              </button>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="empty">Ek bulunmuyor.</p>
                  )}
                </section>

                <section className="bug-panel bug-logs">
                  <div className="bug-panel-head">
                    <h2>İşlem Geçmişi</h2>
                    <span className="bug-panel-chip">{displayedLogs.length} kayıt</span>
                  </div>
                  {displayedLogs.length > 0 ? (
                    <div className="logs-timeline">
                      {displayedLogs.map((log, index) => (
                        <div key={`${index}-${log.__synthetic ? "s" : "r"}`} className="log-item">
                          <div className="log-dot" aria-hidden="true" />
                          <div className="log-content">
                            <p className="log-title">
                              {String(log.action || "").toLowerCase() === "updated" ||
                                String(log.action || "").toLowerCase() === "edited"
                                ? "İş Güncellendi"
                                : String(log.action || "").toLowerCase() === "commented" ||
                                  String(log.action || "").toLowerCase() === "comment"
                                  ? "Yorum Eklendi"
                                : String(log.action || "").toLowerCase() === "added" ||
                                  String(log.action || "").toLowerCase() === "created"
                                  ? "İş Eklendi"
                                  : "Bilinmeyen hareket"}
                            </p>
                            <div className="log-meta">
                              <span className="log-user">{log.user_name}</span>
                              <span className="log-sep">•</span>
                              <span className="log-timestamp">
                                {log.created_at_formatted || formatDateTimeTR(log.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="empty">Herhangi bir işlem kaydı bulunmuyor.</p>
                  )}
                </section>

                <section
                  className={`bug-panel bug-comments is-foldable${comments.length ? "" : " is-hidden"}${panelOpen("comments", comments.length) ? " is-open" : " is-collapsed"}`}
                >
                  <button
                    type="button"
                    className="bug-panel-head bug-panel-toggle"
                    onClick={() => togglePanel("comments")}
                    aria-expanded={panelOpen("comments", comments.length)}
                  >
                    <h2>Yorumlar</h2>
                    <span className="bug-panel-chip">{comments.length} yorum</span>
                    <FiChevronDown className="bug-panel-caret" size={15} aria-hidden="true" />
                  </button>

                  {canComment ? (
                    <form className="comment-form" onSubmit={handleCommentSubmit}>
                      <textarea
                        className="comment-input"
                        value={commentDraft}
                        onChange={(event) => setCommentDraft(event.target.value)}
                        placeholder="İşe dair kısa bir not, geri bildirim ya da takip bilgisi bırak."
                        rows={4}
                        disabled={commentBusy}
                      />
                      <div className="comment-composer-footer">
                        <div className="comment-composer-tools">
                          <input
                            ref={commentFileInputRef}
                            type="file"
                            hidden
                            onChange={(event) =>
                              setCommentFile(event.target.files?.[0] || null)
                            }
                            disabled={commentBusy}
                          />
                          <button
                            type="button"
                            className="comment-attach-btn"
                            onClick={() => commentFileInputRef.current?.click()}
                            disabled={commentBusy}
                            title="Yoruma dosya ekle"
                          >
                            <FiPaperclip size={14} aria-hidden="true" />
                            <span>{commentFile ? fileNameFromPath(commentFile.name) : "Dosya ekle"}</span>
                          </button>
                          {commentFile && (
                            <button
                              type="button"
                              className="comment-attach-remove"
                              onClick={() => {
                                setCommentFile(null);
                                if (commentFileInputRef.current) commentFileInputRef.current.value = "";
                              }}
                              disabled={commentBusy}
                              aria-label="Seçili dosyayı kaldır"
                              title="Seçili dosyayı kaldır"
                            >
                              <FiX size={14} aria-hidden="true" />
                            </button>
                          )}
                        </div>
                        <button
                          type="submit"
                          className="comment-submit"
                          disabled={commentBusy || (!String(commentDraft || "").trim() && !commentFile)}
                        >
                          {commentBusy ? "Gönderiliyor..." : "Yorum Ekle"}
                        </button>
                      </div>
                    </form>
                  ) : (
                    <p className="empty comment-access">
                      Misafir oturumunda yorumlar sadece görüntülenebilir.
                    </p>
                  )}

                  {comments.length > 0 ? (
                    <ul className="comment-list">
                      {comments.map((comment) => {
                        const ownerId = String(comment?.user_id ?? "").trim();
                        const commentId = String(comment?.id ?? "").trim();
                        const canDelete = Boolean(currentUserId) && ownerId === currentUserId;
                        const isDeleting = commentDeleteId === commentId;

                        return (
                          <li key={commentId || `${ownerId}-${comment.created_at || ""}`} className="comment-item">
                            <div className="comment-item-head">
                              <div className="comment-item-meta">
                                <span className="comment-author">
                                  {comment.commenter_name || "İsimsiz kullanıcı"}
                                </span>
                                <span className="comment-date">
                                  {formatDateTimeTR(comment.created_at)}
                                </span>
                              </div>

                              {canDelete && (
                                <button
                                  type="button"
                                  className="comment-delete"
                                  onClick={() => handleCommentDelete(commentId)}
                                  disabled={isDeleting}
                                  aria-label="Yorumu sil"
                                  title="Yorumu sil"
                                >
                                  <FiTrash2 size={14} aria-hidden="true" />
                                  <span>{isDeleting ? "Siliniyor..." : "Sil"}</span>
                                </button>
                              )}
                            </div>

                            <p className="comment-content">{comment.content}</p>

                            {Array.isArray(comment.attachments) && comment.attachments.length > 0 && (
                              <ul className="attachment-list comment-attachment-list">
                                {comment.attachments.map((attachment) => (
                                  <li key={attachment.id} className="attachment-item">
                                    <FiPaperclip className="att-icon" aria-hidden="true" />
                                    <button
                                      type="button"
                                      className="attachment-preview-button"
                                      title={attachment.file_path}
                                      onClick={() =>
                                        openFilePreview(
                                          attachment.file_path,
                                          fileNameFromPath(attachment.file_path)
                                        )
                                      }
                                    >
                                      {fileNameFromPath(attachment.file_path)}
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <p className="empty">Bu iş için henüz yorum eklenmemiş.</p>
                  )}
                </section>
              </>
            )}

            {!loading && !bug && <div className="empty error">Kayıt bulunamadı.</div>}
          </div>
        </div>
      </div>
      <FilePreviewModal
        open={filePreview.open}
        close={() => setFilePreview({ open: false, url: "", title: "", kind: "" })}
        url={filePreview.url}
        title={filePreview.title}
        kind={filePreview.kind}
      />
    </>
  );
};

export default BugDetail;
